<template>
  <b-card class="presales-daily-chart" v-if="hasRight(user, 'dashboard', 'view')" no-body>
    <template #header>
      <h6 class="mb-0">Daily Activity (Presales)</h6>
    </template>
    <b-card-body>
      <apexchart type="bar" :options="chartOptions" :series="safeSeries"></apexchart>
    </b-card-body>
  </b-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { BCard, BCardBody } from 'bootstrap-vue';
import { hasRight } from '@/utils/auth';
import { dashboardGet } from './dashboardApi';

export default {
  name: 'PresalesDailyChart',
  components: {
    BCard,
    BCardBody,
    apexchart: VueApexCharts,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    dashboardData: { type: Object, default: null },
    useAggregate: { type: Boolean, default: false },
    baseApi: {
      type: String,
      default: () => process.env.VUE_APP_APIENDPOINT || '',
    },
    accessToken: {
      type: String,
      default: () => localStorage.getItem('accessToken') || '',
    },
  },
  data() {
    return {
      chartOptions: {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: {
            show: false,
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              legend: {
                position: 'bottom',
                offsetX: -10,
                offsetY: 0,
              },
            },
          },
        ],
        xaxis: {
          categories: [], // Dates will go here
        },
        yaxis: {
          title: {
            text: 'Count',
          },
        },
        fill: {
          opacity: 1,
        },
        legend: {
          position: 'right',
          offsetX: 0,
          offsetY: 50,
        },
      },
      series: [],
    };
  },
  computed: {
    safeSeries() {
      const s = this.series || [];
      const mapped = s.map((item) => ({ name: item.name || '', data: Array.isArray(item.data) ? item.data : [] }));
      return mapped.length ? mapped : [{ name: '', data: [] }];
    },
  },
  watch: {
    dashboardData: {
      deep: true,
      handler(val) {
        if (val && val.presales_daily_chart !== undefined && hasRight(this.user, 'dashboard', 'view')) {
          this.processChartData(this.normalizeToArray(val.presales_daily_chart));
        }
      },
    },
    filters: {
      deep: true,
      immediate: true,
      handler() {
        if (!hasRight(this.user, 'dashboard', 'view')) return;
        if (this.useAggregate) {
          if (this.dashboardData && this.dashboardData.presales_daily_chart !== undefined) this.processChartData(this.normalizeToArray(this.dashboardData.presales_daily_chart));
          return;
        }
        if (this.dashboardData && this.dashboardData.presales_daily_chart !== undefined) { this.processChartData(this.normalizeToArray(this.dashboardData.presales_daily_chart)); return; }
        this.fetchPresalesDailyChartData();
      },
    },
    user: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (hasRight(newVal, 'dashboard', 'view')) {
          if (this.useAggregate) {
            if (this.dashboardData && this.dashboardData.presales_daily_chart !== undefined) this.processChartData(this.normalizeToArray(this.dashboardData.presales_daily_chart));
            return;
          }
          this.fetchPresalesDailyChartData();
        } else {
          this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
          this.series = [];
        }
      },
    },
  },
  methods: {
    hasRight, // Make the helper available in the template
    normalizeToArray(val) {
      if (Array.isArray(val)) return val;
      if (val && typeof val === 'object') return Object.values(val);
      return [];
    },
    async fetchPresalesDailyChartData() {
      if (!hasRight(this.user, 'dashboard', 'view')) return;
      if (this.useAggregate || (this.dashboardData && this.dashboardData.presales_daily_chart !== undefined)) {
        if (this.dashboardData && this.dashboardData.presales_daily_chart !== undefined) this.processChartData(this.normalizeToArray(this.dashboardData.presales_daily_chart));
        return;
      }
      try {
        const response = await dashboardGet(this.baseApi, this.accessToken, '/dashboard/crm/presales-daily-chart', this.filters);
        const raw = response.data && response.data.data;
        this.processChartData(this.normalizeToArray(raw));
      } catch (error) {
        console.error('Error fetching presales daily chart data:', error);
        // Handle error
      }
    },
    processChartData(data) {
      const arr = Array.isArray(data) ? data : [];
      if (arr.length === 0) {
        this.series = [];
        this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
        return;
      }
      // API format: [{ date, Call, Whatsapp, SMS, Email, ... }, ...]
      const categories = [...new Set(arr.map(item => item && item.date).filter(Boolean))].sort();
      const typeKeys = new Set();
      arr.forEach(item => {
        if (item && typeof item === 'object') {
          Object.keys(item).forEach(k => { if (k !== 'date') typeKeys.add(k); });
        }
      });
      const allTypes = [...typeKeys];

      const dateToIndex = {};
      categories.forEach((d, i) => { dateToIndex[d] = i; });

      const seriesMap = {};
      allTypes.forEach(type => {
        seriesMap[type] = new Array(categories.length).fill(0);
      });

      arr.forEach(item => {
        if (!item || !item.date) return;
        const dateIndex = dateToIndex[item.date];
        if (dateIndex === undefined) return;
        allTypes.forEach(type => {
          const val = item[type];
          seriesMap[type][dateIndex] = Number(val) || 0;
        });
      });

      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          categories,
        },
      };

      this.series = allTypes.map(type => ({
        name: type,
        data: seriesMap[type],
      }));
    },
  },
};
</script>

<style scoped>
.presales-daily-chart {
  margin-top: 20px;
}
</style>